import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, Search, ShieldCheck, Pencil, RefreshCw } from "lucide-react";
import "./admin.users.css";
import * as Dialog from "@radix-ui/react-dialog";
import {
  AdminUserError,
  listAdminUsers,
  setAdminUserActive,
  updateAdminUser,
  type AdminUser,
  type UserFilters,
} from "../lib/admin-users-api";
import { fmtDate } from "../lib/format-date";
import { sanitizePhone, isValidPhone, PHONE_INPUT_PROPS } from "../lib/phone";

export const Route = createFileRoute("/admin/users")({ component: Page });

function Page() {
  const qc = useQueryClient();
  const [filters, setFilters] = useState<UserFilters>({
    account_type: "all",
    is_active: "all",
    search: "",
    page: 1,
    page_size: 25,
  });
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState<AdminUser | null>(null);
  useEffect(() => {
    const timer = setTimeout(
      () => setFilters((f) => ({ ...f, search: search.trim(), page: 1 })),
      300,
    );
    return () => clearTimeout(timer);
  }, [search]);
  const query = useQuery({
    queryKey: ["jgy-admin-users", filters],
    queryFn: ({ signal }) => listAdminUsers(filters, signal),
  });
  const data = query.data;
  const pages = Math.max(1, Math.ceil((data?.count ?? 0) / filters.page_size));
  useEffect(() => {
    if (data && filters.page > pages) {
      setFilters((f) => ({ ...f, page: pages }));
    }
  }, [data, filters.page, pages]);
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["jgy-admin-users"] }),
      qc.invalidateQueries({ queryKey: ["jgy-members"] }),
      qc.invalidateQueries({ queryKey: ["jgy-volunteers"] }),
    ]);
  const onError = (error: unknown) =>
    toast.error(error instanceof Error ? error.message : "Something went wrong.");
  const status = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setAdminUserActive(id, active),
    onSuccess: (result) => {
      toast.success(result.detail);
      return invalidate();
    },
    onError: (error) => {
      onError(error);
      if (
        error instanceof AdminUserError &&
        ["already_active", "already_inactive"].includes(error.code ?? "")
      ) {
        void invalidate();
      }
    },
  });
  const save = useMutation({
    mutationFn: (user: AdminUser) =>
      updateAdminUser(user.id, { full_name: user.full_name.trim(), phone: user.phone }),
    onSuccess: () => {
      setEdit(null);
      toast.success("Profile updated.");
      return invalidate();
    },
    onError,
  });
  const changeFilter = (patch: Partial<UserFilters>) =>
    setFilters((f) => ({ ...f, ...patch, page: 1 }));

  return (
    <div className="user-management space-y-5">
      <header className="um-header">
        <div>
          <div className="um-eyebrow">PEOPLE & COMMUNITY</div>
          <h2>
            User management<span>.</span>
          </h2>
          <p>A connected community. Thoughtfully managed.</p>
        </div>
        <button
          className="um-refresh"
          disabled={query.isFetching}
          onClick={() => void query.refetch()}
        >
          <RefreshCw size={15} className={query.isFetching ? "animate-spin" : ""} /> Refresh
        </button>
      </header>
      <section className="um-intro" aria-label="Account management overview">
        <span className="um-intro-icon">
          <Users size={28} strokeWidth={1.5} />
        </span>
        <div>
          <h3>Every account, one place</h3>
          <p>Manage profiles and login access for your devotees and volunteers.</p>
        </div>
        <span className="um-intro-note">
          <ShieldCheck size={18} /> Admin access
        </span>
      </section>
      <section className="um-directory" aria-label="Account directory">
        <div className="um-directory-heading">
          <div>
            <h3>
              Account directory <span>{data?.count.toLocaleString() ?? "—"}</span>
            </h3>
            <p>Find people and manage their access.</p>
          </div>
          <span className="um-directory-note">Devotees & volunteers</span>
        </div>
        <div className="um-toolbar flex flex-wrap items-end gap-3">
          <label className="flex min-w-56 flex-1 flex-col gap-1 text-sm">
            <span className="flex items-center gap-2">
              <Search size={14} /> Search users
            </span>
            <input
              type="search"
              className="minp"
              placeholder="Search name, email, phone or username..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Account type
            <select
              className="minp"
              value={filters.account_type}
              onChange={(e) =>
                changeFilter({ account_type: e.target.value as UserFilters["account_type"] })
              }
            >
              <option value="all">All accounts</option>
              <option value="devotee">Devotees</option>
              <option value="volunteer">Volunteers</option>
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm">
            Login status
            <select
              className="minp"
              value={filters.is_active}
              onChange={(e) =>
                changeFilter({ is_active: e.target.value as UserFilters["is_active"] })
              }
            >
              <option value="all">All statuses</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </label>
        </div>
        <div className="um-table-wrap overflow-x-auto">
          <table
            className="w-full min-w-[850px] text-sm"
            aria-label="Login accounts"
            aria-busy={query.isFetching}
          >
            <thead className="bg-muted text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                {[
                  "User",
                  "Email",
                  "Phone",
                  "Account type",
                  "Login status",
                  "Registered",
                  "Actions",
                ].map((heading) => (
                  <th key={heading} className="p-3">
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {query.isLoading && (
                <tr>
                  <td colSpan={7} className="p-8 text-center">
                    Loading users...
                  </td>
                </tr>
              )}
              {query.isError && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-destructive" role="alert">
                    {query.error.message}{" "}
                    <button className="underline" onClick={() => void query.refetch()}>
                      Retry
                    </button>
                  </td>
                </tr>
              )}
              {!query.isLoading && !query.isError && data?.results.length === 0 && (
                <tr>
                  <td colSpan={7} className="p-8 text-center text-muted-foreground">
                    <div className="um-empty">
                      <Search size={30} />
                      <strong>No accounts found</strong>
                      <span>Try another name or adjust your filters.</span>
                    </div>
                  </td>
                </tr>
              )}
              {!query.isError &&
                data?.results.map((user) => (
                  <tr key={user.id} className="border-t border-border hover:bg-muted/40">
                    <td data-label="Name" className="p-3">
                      <div className="um-person">
                        <span
                          className={`um-avatar ${user.account_type === "volunteer" ? "um-avatar-volunteer" : ""}`}
                        >
                          {(user.full_name || user.username || "U")
                            .trim()
                            .split(/\s+/)
                            .slice(0, 2)
                            .map((part) => part[0])
                            .join("")
                            .toUpperCase()}
                        </span>
                        <div>
                          <div className="font-semibold">{user.full_name || "Unnamed"}</div>
                          <div className="text-xs text-muted-foreground">{user.username}</div>
                        </div>
                      </div>
                    </td>
                    <td data-label="Email" className="p-3">
                      {user.email || "—"}
                    </td>
                    <td data-label="Phone" className="p-3">
                      {user.phone || "—"}
                    </td>
                    <td data-label="Account type" className="p-3">
                      <span
                        className={`um-role ${user.account_type === "volunteer" ? "um-role-volunteer" : ""}`}
                      >
                        {user.account_type === "volunteer" ? "Volunteer" : "Devotee"}
                      </span>
                    </td>
                    <td data-label="Login status" className="p-3">
                      <span className={`um-status ${user.is_active ? "is-enabled" : ""}`}>
                        {user.is_active ? "Enabled" : "Disabled"}
                      </span>
                    </td>
                    <td data-label="Registered" className="whitespace-nowrap p-3">
                      {fmtDate(user.created_at) || "—"}
                    </td>
                    <td data-label="Actions" className="p-3">
                      <div className="flex gap-2">
                        <button
                          className="um-edit"
                          disabled={status.isPending || save.isPending}
                          onClick={() =>
                            setEdit({
                              ...user,
                              full_name: user.full_name || "",
                              phone: user.phone || "",
                            })
                          }
                        >
                          <Pencil size={13} /> Edit
                        </button>
                        <button
                          className={`um-action ${user.is_active ? "" : "um-action-activate"}`}
                          disabled={status.isPending || save.isPending}
                          aria-label={`${user.is_active ? "Deactivate" : "Activate"} ${user.full_name || user.username}`}
                          onClick={() => status.mutate({ id: user.id, active: !user.is_active })}
                        >
                          {status.isPending && status.variables.id === user.id
                            ? "Updating..."
                            : user.is_active
                              ? "Deactivate"
                              : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        <div className="um-pagination flex flex-wrap items-center justify-between gap-3 text-sm">
          <span aria-live="polite">
            {data ? `${data.count} accounts · Page ${data.page} of ${pages}` : ""}
          </span>
          <div className="flex items-center gap-3">
            <label>
              Per page{" "}
              <select
                className="rounded-lg border border-border bg-card p-2"
                value={filters.page_size}
                onChange={(e) => changeFilter({ page_size: Number(e.target.value) })}
              >
                {[25, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="btn-outline py-2 text-xs disabled:opacity-50"
              disabled={filters.page === 1 || query.isFetching}
              onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}
            >
              Previous
            </button>
            <button
              className="btn-outline py-2 text-xs disabled:opacity-50"
              disabled={!data || query.isError || filters.page >= pages || query.isFetching}
              onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}
            >
              Next
            </button>
          </div>
        </div>
      </section>
      <p className="um-footnote">
        <ShieldCheck size={14} /> Login access can be updated at any time.
      </p>
      <Dialog.Root
        open={!!edit}
        onOpenChange={(open) => {
          if (!open && !save.isPending) {
            setEdit(null);
          }
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="um-dialog-overlay fixed inset-0 z-50" />
          <Dialog.Content className="um-dialog fixed left-1/2 top-1/2 z-50 w-[calc(100%_-_2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 space-y-4 rounded-2xl bg-card p-6">
            <div>
              <span className="um-intro-icon">
                <Pencil size={22} />
              </span>
              <Dialog.Title className="um-dialog-title">Edit profile</Dialog.Title>
              <Dialog.Description className="um-dialog-description">
                Update the account name and phone number.
              </Dialog.Description>
            </div>
            {edit && (
              <form
                className="space-y-4"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!save.isPending) {
                    save.mutate(edit);
                  }
                }}
              >
                <p className="text-sm text-muted-foreground">{edit.email || edit.username}</p>
                <label className="block text-sm">
                  Full name
                  <input
                    className="minp mt-1"
                    required
                    value={edit.full_name}
                    onChange={(e) => setEdit({ ...edit, full_name: e.target.value })}
                  />
                </label>
                <label className="block text-sm">
                  Phone
                  <input
                    {...PHONE_INPUT_PROPS}
                    className="minp mt-1"
                    value={edit.phone}
                    onChange={(e) => setEdit({ ...edit, phone: sanitizePhone(e.target.value) })}
                  />
                </label>
                <button
                  type="submit"
                  className="btn-primary py-2 text-sm"
                  disabled={
                    save.isPending ||
                    !edit.full_name.trim() ||
                    (!!edit.phone && !isValidPhone(edit.phone))
                  }
                >
                  {save.isPending ? "Saving..." : "Save changes"}
                </button>
              </form>
            )}
            <Dialog.Close className="btn-outline py-2 text-sm" disabled={save.isPending}>
              Close
            </Dialog.Close>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
